// src/components/Stepper.tsx
import React, { type ReactNode, useEffect, useMemo, useState } from "react";

type StepOnClickResult = boolean | void;

export interface Step {
    label: React.ReactNode;
    content: ReactNode;
    validator: () => boolean;
    path?: string;
    condition?: () => boolean;
    onClick?: () => Promise<StepOnClickResult>;
    ctaText?: string;        // defaults to "Submit" (or "Finish" on last step)
    /** Hide the Back button for this step */
    hideBack?: boolean;
}

export interface StepperNav {
    goNext: () => void;
    goBack: () => void;
    loading: boolean;
}
export const StepperNavContext = React.createContext<StepperNav>({
    goNext: () => { },
    goBack: () => { },
    loading: false,
});
interface StepperProps {
    steps: Step[];
    initialStepIndex?: number;
    initialCompleted?: boolean[];
    onFinish?: (success: boolean) => void | Promise<void>;


}

const StepperKyc: React.FC<StepperProps> = ({
    steps,
    initialStepIndex = 0,
    initialCompleted,
    onFinish,
}) => {
    // filter steps based on condition; keep it memoized
    const filteredSteps = useMemo(
        () => steps.filter((s) => (s.condition ? s.condition() : true)),
        [steps]
    );
    const stepRefs = React.useRef<(HTMLDivElement | null)[]>([]);

    // state
    const [currentStepIndex, setCurrentStepIndex] = useState(initialStepIndex);
    const [completedSteps, setCompletedSteps] = useState<boolean[]>(
        initialCompleted ??
        // keep completion flags aligned to *filtered* list length
        new Array(filteredSteps.length).fill(false)
    );
    const [loading, setLoading] = useState(false);

    // keep index in range if filters or initial index change
    useEffect(() => {
        setCurrentStepIndex((idx) =>
            initialStepIndex < filteredSteps.length
                ? initialStepIndex
                : Math.min(idx, filteredSteps.length - 1)
        );
    }, [initialStepIndex, filteredSteps.length]);

    // keep completed flags in sync with current filtered length
    useEffect(() => {
        if (initialCompleted && initialCompleted.length === filteredSteps.length) {
            setCompletedSteps(initialCompleted);
        } else {
            setCompletedSteps((prev) => {
                const next = new Array(filteredSteps.length).fill(false);
                for (let i = 0; i < Math.min(prev.length, next.length); i++) next[i] = prev[i];
                return next;
            });
        }
    }, [initialCompleted, filteredSteps.length]);

    const currentStep = filteredSteps[currentStepIndex];
    const isFirstStep = currentStepIndex === 0;
    const isLastStep = currentStepIndex === filteredSteps.length - 1;

    const nextStep = async () => {
        if (!currentStep) return;
        if (!currentStep.validator || !currentStep.validator()) return;

        setLoading(true);
        try {
            let proceed = true;
            if (currentStep.onClick) {
                const out = await currentStep.onClick();
                proceed = out !== false;
            }

            if (!proceed) return;

            // mark current as completed
            setCompletedSteps((prev) => {
                const updated = [...prev];
                updated[currentStepIndex] = true;
                return updated;
            });

            if (isLastStep) {
                await onFinish?.(true); // <-- send success=true
            } else {
                setCurrentStepIndex((i) => Math.min(i + 1, filteredSteps.length - 1));
            }
        } finally {
            setLoading(false);
        }
    };

    const previousStep = () => {
        if (currentStepIndex > 0) setCurrentStepIndex((i) => i - 1);
    };

    const shouldShowBack = !isFirstStep && !currentStep?.hideBack;

    // Button label: step.ctaText or sensible default
    const submitLabel = currentStep?.ctaText ?? (isLastStep ? "Finish" : "Submit");

    useEffect(() => {
        const el = stepRefs.current[currentStepIndex];
        if (!el) return;

        el.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
        });

    }, [currentStepIndex]);


    return (
        <StepperNavContext.Provider value={{ goNext: nextStep, goBack: previousStep, loading }}>
            <div className="flex flex-col bg-white border border-border-subtle rounded-lg">

                {/* STEPS HEADER */}
                <div className="flex gap-4 overflow-x-auto px-4 py-3 border-b">
                    {filteredSteps.map((step, index) => {
                        const isCompleted = completedSteps[index];
                        const isCurrent = index === currentStepIndex;

                        return (
                            <div
                                key={index}
                                ref={(el) => {
                                    stepRefs.current[index] = el;
                                }}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border whitespace-nowrap
${isCurrent ? "border-border-button-primary" : "border-border-subtle"}
${isCompleted && "bg-bg-button-secondary"}
`}
                            >
                                <div className="w-6 h-6 rounded-full flex items-center justify-center bg-bg-base">
                                    {index + 1}
                                </div>

                                {isCurrent && (
                                    <span className="text-sm font-semibold">
                                        {step.label}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* CURRENT STEP CONTENT */}
                <div
                    key={currentStepIndex}
                    className="p-4 transition-all duration-300 ease-out animate-step"
                >

                    <h2 className="text-lg font-semibold mb-4 text-center md:text-left">
                        {currentStep?.label}
                    </h2>

                    {currentStep?.content}
                </div>

                {/* FOOTER ACTIONS */}
                <div className="flex gap-2 p-4 border-t">
                    {shouldShowBack && (
                        <button
                            onClick={previousStep}
                            disabled={loading}
                            className="flex-1 border rounded-lg h-10"
                        >
                            BACK
                        </button>
                    )}

                    <button
                        onClick={nextStep}
                        disabled={!currentStep?.validator() || loading}
                        className="flex-1 rounded-lg h-10 bg-bg-button-primary"
                    >
                        {submitLabel}
                    </button>
                </div>
            </div>
        </StepperNavContext.Provider>
    );

};

export default StepperKyc;
